import React, { useMemo, useState } from "react";
import Layout, { Container } from "../components/layout";
import { apiurl } from "../config/config";


export default function EmployeeManagement({
  endpoint = `${apiurl}/api/employee/bulk-employee-inactive`,
  title = "Bulk Inactivate / Update Employees via Excel",
}) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [summary, setSummary] = useState(null);
  const [success, setSuccess] = useState([]);
  const [failed, setFailed] = useState([]);
  const [notFound, setNotFound] = useState([]);

  const [activeTab, setActiveTab] = useState("success");
  const [q, setQ] = useState("");

  const onFileChange = (e) => {
    setFile(e.target.files?.[0] || null);
    setError("");
  };

  const reset = () => {
    setFile(null);
    setSummary(null);
    setSuccess([]);
    setFailed([]);
    setNotFound([]);
    setQ("");
    setActiveTab("success");
  };

  const downloadTemplate = () => {
    const headers = [
      "EmployeeCode",
      "EmployeeName",
      "Status",
      "Reason",
      "ClearanceStatus",
    ];
    const rows = [
      ["EMP001", "Amit Kumar", "inactive", "Resigned", "completed"],
      ["EMP002", "Neha Singh", "on-hold", "Pending clearance", "in-progress"],
    ];
    const csv = [headers.join(","), ...rows.map((r) => r.map(csvEscape).join(","))].join("\n");
    triggerDownload(csv, "inactive-employee-template.csv", "text/csv;charset=utf-8;");
  };

  const handleUpload = async () => {
    try {
      if (!file) return setError("Please select an Excel file (.xlsx/.xls) to upload.");
      setLoading(true);
      setError("");

      const form = new FormData();
      form.append("file", file);

      const res = await fetch(endpoint, {
        method: "POST",
        body: form,
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `Upload failed with status ${res.status}`);
      }

      const data = await res.json();
      setSummary({
        total: data.total ?? 0,
        successCount: data.successCount ?? (data.success?.length || 0),
        failedCount: data.failedCount ?? (data.failed?.length || 0),
        notFoundCount: data.notFoundCount ?? (data.notFound?.length || 0),
      });
      setSuccess(data.success || []);
      setFailed(data.failed || []);
      setNotFound(data.notFound || []);
    } catch (e) {
      setError(e?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const lists = useMemo(() => ({ success, failed, notFound }), [success, failed, notFound]);

  const filtered = useMemo(() => {
    const list = lists[activeTab] || [];
    if (!q.trim()) return list;
    const needle = q.toLowerCase();
    return list.filter((r) =>
      Object.values(r || {}).some((v) => String(v ?? "").toLowerCase().includes(needle))
    );
  }, [lists, activeTab, q]);

  const exportCSV = (which) => {
    const rows = lists[which] || [];
    if (!rows.length) return;

    const headers = Array.from(
      rows.reduce((set, obj) => {
        Object.keys(obj || {}).forEach((k) => set.add(k));
        return set;
      }, new Set())
    );
    const csvBody = rows
      .map((row) => headers.map((h) => csvEscape(row?.[h])).join(","))
      .join("\n");
    const csv = `${headers.join(",")}\n${csvBody}`;
    triggerDownload(csv, `${which}-list.csv`, "text/csv;charset=utf-8;");
  };

  return (
    <Layout>
        <Container overflow={"overflow-scroll"}>


    <div className="w-full max-w-6xl mx-auto p-4 sm:p-6">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">{title}</h1>
        <p className="text-sm text-gray-500 mt-1">Upload an Excel with columns: <span className="font-medium">EmployeeCode, EmployeeName, Status, Reason, ClearanceStatus</span>. We will find by code, update if found, or list as Not Found.</p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <label className="inline-flex items-center px-3 py-2 rounded-xl bg-gray-50 border border-dashed border-gray-300 cursor-pointer hover:bg-gray-100 transition">
              <input type="file" accept=".xlsx,.xls,.csv" onChange={onFileChange} className="hidden" />
              <span className="text-sm font-medium">{file ? file.name : "Choose Excel file"}</span>
            </label>
            {file && (
              <button onClick={() => setFile(null)} className="text-sm text-gray-500 hover:text-gray-700 underline">Clear</button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button onClick={downloadTemplate} className="px-3 py-2 text-sm rounded-xl bg-gray-100 hover:bg-gray-200">Download Template</button>
            <button onClick={reset} className="px-3 py-2 text-sm rounded-xl bg-white border hover:bg-gray-50">Reset</button>
            <button
              onClick={handleUpload}
              disabled={loading || !file}
              className={`px-4 py-2 text-sm rounded-xl text-white transition ${loading || !file ? "bg-gray-400 cursor-not-allowed" : "bg-black hover:bg-gray-800"}`}
            >
              {loading ? "Uploading…" : "Upload & Process"}
            </button>
          </div>
        </div>

        {error && (
          <div className="mt-4 p-3 rounded-xl bg-red-50 text-red-700 text-sm border border-red-100">{error}</div>
        )}

        {summary && (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard label="Total Rows" value={summary.total} />
            <StatCard label="Success" value={summary.successCount} badgeClass="bg-emerald-100 text-emerald-700" />
            <StatCard label="Failed" value={summary.failedCount} badgeClass="bg-rose-100 text-rose-700" />
            <StatCard label="Not Found" value={summary.notFoundCount} badgeClass="bg-amber-100 text-amber-800" />
          </div>
        )}

        {summary && (
          <div className="mt-6">
            <div className="flex flex-wrap items-center gap-2">
              <TabButton active={activeTab === "success"} onClick={() => setActiveTab("success")}>Success</TabButton>
              <TabButton active={activeTab === "failed"} onClick={() => setActiveTab("failed")}>Failed</TabButton>
              <TabButton active={activeTab === "notFound"} onClick={() => setActiveTab("notFound")}>Not Found</TabButton>

              <div className="ms-auto flex items-center gap-2 w-full sm:w-auto">
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search results…"
                  className="w-full sm:w-64 px-3 py-2 rounded-xl border bg-white focus:outline-none focus:ring-2 focus:ring-black"
                />
                <button onClick={() => exportCSV(activeTab)} className="px-3 py-2 text-sm rounded-xl bg-gray-100 hover:bg-gray-200">Export CSV</button>
              </div>
            </div>

            <div className="mt-3 overflow-auto border rounded-2xl">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <Th>EmployeeCode</Th>
                    <Th>EmployeeName</Th>
                    {activeTab === "success" && <Th>Status</Th>}
                    {activeTab !== "success" && <Th>Reason</Th>}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center p-6 text-gray-500">No records</td>
                    </tr>
                  ) : (
                    filtered.map((row, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <Td mono>{row.EmployeeCode ?? "-"}</Td>
                        <Td>{row.EmployeeName ?? "-"}</Td>
                        {activeTab === "success" ? (
                          <Td>
                            <Badge>{row.status || "Processed"}</Badge>
                          </Td>
                        ) : (
                          <Td className="max-w-[420px]">
                            <span className="line-clamp-2" title={row.reason || "-"}>{row.reason || "-"}</span>
                          </Td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <FooterNote />
    </div>
            </Container>
    </Layout>
  );
}

function StatCard({ label, value, badgeClass = "bg-gray-100 text-gray-800" }) {
  return (
    <div className="rounded-2xl border p-4 bg-white">
      <div className="text-sm text-gray-500">{label}</div>
      <div className={`inline-flex items-center mt-2 px-3 py-1 rounded-full text-sm font-medium ${badgeClass}`}>
        {Number(value ?? 0).toLocaleString()}
      </div>
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={
        `px-3 py-2 text-sm rounded-xl border transition ${
          active ? "bg-black text-white border-black" : "bg-white hover:bg-gray-50"
        }`
      }
    >
      {children}
    </button>
  );
}

function Th({ children }) {
  return <th className="text-left font-semibold px-4 py-3 whitespace-nowrap">{children}</th>;
}

function Td({ children, mono = false, className = "" }) {
  return (
    <td className={`px-4 py-3 align-top ${mono ? "font-mono" : ""} ${className}`}>{children}</td>
  );
}

function Badge({ children }) {
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-emerald-100 text-emerald-700">{children}</span>
  );
}

function FooterNote() {
  return (
    <p className="text-xs text-gray-500 mt-4">
      Tip: Keep <span className="font-medium">Status</span> values lowercase (e.g., "inactive", "on-hold") and
      <span className="font-medium"> ClearanceStatus</span> as one of: "pending", "in-progress", "completed".
    </p>
  );
}

// ---------- utils ----------
function csvEscape(val) {
  const s = String(val ?? "");
  if (/[",\n]/.test(s)) return '"' + s.replaceAll('"', '""') + '"';
  return s;
}

function triggerDownload(content, filename, mime) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}